<template>
  <div class="relative">
    <label
      v-if="label"
      class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
    >
      {{ label }}
      <span
        v-if="showLockedMessage && isLocked"
        class="text-xs text-orange-600 dark:text-orange-400 ml-2"
      >
        (Locked - EBM metadata exists)
      </span>
    </label>

    <div class="relative">
      <!-- Search Input -->
      <div class="relative">
        <input
          ref="searchInput"
          :value="displayValue"
          @input="handleInput"
          type="text"
          :placeholder="placeholder"
          :disabled="disabled"
          class="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400 disabled:cursor-not-allowed"
          :class="{
            'border-red-500': hasError,
            'border-green-500': isValid && !hasError,
          }"
          @focus="showDropdown = true"
          @blur="handleBlur"
          @keydown.enter.prevent="selectHighlightedOrFirst"
          @keydown.arrow-down.prevent="highlightNext"
          @keydown.arrow-up.prevent="highlightPrevious"
          @keydown.escape="closeDropdown"
        />

        <!-- Dropdown Toggle Button -->
        <button
          type="button"
          :disabled="disabled"
          class="absolute right-8 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:cursor-not-allowed"
          @click="toggleDropdown"
        >
          <vue-feather
            :type="showDropdown ? 'chevron-up' : 'chevron-down'"
            size="16"
          />
        </button>

        <!-- Refresh Button -->
        <button
          type="button"
          :disabled="disabled || loading"
          class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:cursor-not-allowed"
          @click="refreshOptions"
          :title="loading ? 'Loading...' : 'Refresh options'"
        >
          <vue-feather
            :type="loading ? 'loader' : 'refresh-cw'"
            :size="16"
            :class="{ 'animate-spin': loading }"
          />
        </button>
      </div>

      <!-- Dropdown Menu -->
      <div
        v-if="showDropdown"
        class="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
      >
        <!-- Loading State -->
        <div
          v-if="loading"
          class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center"
        >
          <vue-feather type="loader" size="16" class="animate-spin mx-auto" />
          Loading options...
        </div>

        <!-- Search Loading State -->
        <div
          v-else-if="searchLoading"
          class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center"
        >
          <vue-feather type="loader" size="16" class="animate-spin mx-auto" />
          Searching...
        </div>

        <!-- Error State -->
        <div
          v-else-if="error"
          class="px-3 py-2 text-sm text-red-600 dark:text-red-400 text-center"
        >
          {{ error }}
          <button
            type="button"
            class="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            @click="refreshOptions"
          >
            Retry
          </button>
        </div>

        <!-- No Options -->
        <div
          v-else-if="filteredOptions.length === 0"
          class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center"
        >
          {{
            debouncedSearchQuery
              ? "No matching options found"
              : "No options available"
          }}
        </div>

        <!-- Options List -->
        <div v-else>
          <button
            v-for="(option, index) in filteredOptions"
            :key="option.cd"
            type="button"
            class="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700 focus:outline-none"
            :class="{
              'bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100':
                highlightedIndex === index,
              'bg-gray-50 dark:bg-gray-700':
                selectedOption && selectedOption.cd === option.cd,
            }"
            @mousedown.prevent="selectOption(option)"
            @mouseenter="highlightedIndex = index"
          >
            <div class="flex justify-between items-center">
              <div>
                <div class="font-medium">{{ option.cd }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ option.cdNm }}
                </div>
              </div>
              <vue-feather
                v-if="selectedOption && selectedOption.cd === option.cd"
                type="check"
                size="14"
                class="text-green-600 dark:text-green-400"
              />
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- Selected Value Display -->
    <div
      v-if="selectedOption && !showDropdown"
      class="mt-1 text-xs text-gray-600 dark:text-gray-400"
    >
      Selected: {{ selectedOption.cdNm }}
    </div>
  </div>
</template>

<script>
import http from "@/helper/http";

export default {
  name: "EbmSelectDropdown",
  props: {
    fieldName: {
      type: String,
      required: true,
    },
    modelValue: {
      type: [String, Number],
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Search and select...",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    isValid: {
      type: Boolean,
      default: false,
    },
    showLockedMessage: {
      type: Boolean,
      default: false,
    },
    isLocked: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      options: [],
      loading: false,
      error: null,
      showDropdown: false,
      searchQuery: "",
      debouncedSearchQuery: "",
      highlightedIndex: -1,
      selectedOption: null,
      isUserEditing: false,
      lastCommittedOption: null,
      typingTimeout: null,
      searchTimeout: null,
      searchLoading: false,
      wasIntentionallyCleared: false,
    };
  },
  computed: {
    filteredOptions() {
      if (!this.debouncedSearchQuery) return this.options;

      const query = this.debouncedSearchQuery.toLowerCase();
      return this.options.filter(
        (option) =>
          option.cd.toLowerCase().includes(query) ||
          option.cdNm.toLowerCase().includes(query)
      );
    },
    displayValue() {
      // Prefer the resolved name; otherwise keep whatever the user typed;
      // as a last resort, show the raw modelValue so it isn't blank.

      if (this.selectedOption) return this.selectedOption.cdNm;
      if (this.searchQuery) return this.searchQuery;
      return this.modelValue ?? "";
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        // Only update if user is not actively editing
        if (!this.isUserEditing) {
          this.updateSelectedOption(newVal);
        }
      },
    },
    options() {
      // Re-resolve the selection whenever options change, unless the user is actively typing
      if (!this.isUserEditing) {
        this.updateSelectedOption(this.modelValue ?? "");
      }
    },
    searchQuery: {
      handler(newVal) {
        // Clear previous search timeout
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
        }

        // Show search loading state for non-empty queries
        if (newVal && newVal.trim()) {
          this.searchLoading = true;
        } else {
          this.searchLoading = false;
          this.debouncedSearchQuery = "";
          return;
        }

        // Debounce the search query update
        this.searchTimeout = setTimeout(() => {
          this.debouncedSearchQuery = newVal;
          this.searchLoading = false;
        }, 300); // 300ms debounce delay
      },
      immediate: true,
    },
    filteredOptions(newVal) {
      if (this.highlightedIndex >= newVal.length) {
        this.highlightedIndex = newVal.length - 1;
      }
      if (this.highlightedIndex < -1) this.highlightedIndex = -1;
    },
  },
  mounted() {
    this.loadOptions();
  },
  beforeUnmount() {
    // Clean up timeouts to prevent memory leaks
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    async loadOptions(forceRefresh = false) {
      if (!this.fieldName) return;

      const cacheKey = `ebm_select_codes_${this.fieldName}`;

      // Check cache first (unless force refresh is requested)
      if (!forceRefresh) {
        const cachedOptions = this.$safeJSONParse(
          localStorage.getItem(cacheKey)
        );

        const normalizedCachedOptions = this.normalizeOptions(cachedOptions);

        if (
          normalizedCachedOptions &&
          Array.isArray(normalizedCachedOptions) &&
          normalizedCachedOptions.length > 0
        ) {
          this.options = normalizedCachedOptions;

          // Always attempt to resolve current value or field default after load
          this.$nextTick(() => {
            this.updateSelectedOption(this.modelValue ?? "");
          });
          return;
        }
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await http.get(
          `/v1/rra/ebm/select-codes/${this.fieldName}`
        );

        const responseData = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        this.options = this.normalizeOptions(responseData);

        // Cache the options for future use
        if (this.options.length > 0) {
          localStorage.setItem(cacheKey, JSON.stringify(this.options));
        }

        // Always attempt to resolve current value or field default after load
        this.$nextTick(() => {
          this.updateSelectedOption(this.modelValue ?? "");
        });
      } catch (error) {
        console.error(
          `❌ Failed to load options for ${this.fieldName}:`,
          error
        );
        this.error = this.extractErrorMessage(error);
        this.options = [];
      } finally {
        this.loading = false;
      }
    },

    refreshOptions() {
      this.loadOptions(true); // Force refresh, bypass cache
    },

    clearCache() {
      const cacheKey = `ebm_select_codes_${this.fieldName}`;
      localStorage.removeItem(cacheKey);
      this.loadOptions(true); // Force refresh after clearing cache
    },

    updateSelectedOption(valueRaw) {
      const value = valueRaw != null ? String(valueRaw).trim() : "";
      if (!value && !this.options.length) return;

      if (value && this.options.length) {
        // compare as strings to avoid number/string mismatches

        this.selectedOption =
          this.options.find((opt) => String(opt.cd ?? "").trim() === value) ||
          null;

        if (this.selectedOption) {
          this.searchQuery = this.selectedOption.cdNm;
          this.lastCommittedOption = this.selectedOption;
          return;
        }

        // If incoming value doesn't match any option:
        // 1) try the field default
        const def = this.getDefaultValueForField();
        if (def && def !== value) {
          const match = this.options.find(
            (opt) => String(opt.cd ?? "").trim() === String(def).trim()
          );
          if (match) {
            this.selectOption(match); // will emit both events
            return;
          }
        }

        // 2) fallback: show raw value so the input isn't blank
        this.searchQuery = value; // visible until user changes it or options refresh
        this.selectedOption = null;
        return;
      }

      // no external value: only set default if this is the initial load
      if (!value) {
        // Only set default on initial load, not when user is actively editing
        if (
          this.options.length > 0 &&
          !this.searchQuery &&
          !this.selectedOption &&
          !this.isUserEditing
        ) {
          const def = this.getDefaultValueForField();
          if (def) {
            const match = this.options.find(
              (opt) => String(opt.cd ?? "").trim() === String(def ?? "").trim()
            );
            if (match) {
              this.selectOption(match);
              return;
            }
          }
        }
        this.selectedOption = null;
        this.searchQuery = "";
      }
    },

    getDefaultValueForField() {
      // Map field names to their default values
      const defaults = {
        orgnNatCd: "RW",
        itemTyCd: "2",
        pkgUnitCd: "NT",
        qtyUnitCd: "U",
        taxTyCd: "B",
        itemClsCd: "5020230602",
        regTyCd: "A",
        pchsTyCd: "N",
        pmtTyCd: "01",
        pchsSttsCd: "02",
        rctTyCd: "P",
      };

      return defaults[this.fieldName] || null;
    },

    handleInput(e) {
      const inputValue = e.target.value;
      this.searchQuery = inputValue;
      this.isUserEditing = true;

      // Handle clearing the field immediately
      if (inputValue.length === 0) {
        this.selectedOption = null;
        this.lastCommittedOption = null;
        this.wasIntentionallyCleared = true;
        // Show dropdown when field is cleared so user can select options
        this.showDropdown = true;
        // Emit empty value to clear the model
        this.$emit("update:modelValue", "");
        return;
      }

      // Reset the cleared flag when user starts typing again
      this.wasIntentionallyCleared = false;

      // Show dropdown when user starts typing
      this.showDropdown = true;

      if (this.selectedOption && inputValue === this.selectedOption.cdNm)
        return;

      this.selectedOption = null;
    },

    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      if (this.showDropdown) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
        });
      }
    },

    selectOption(option) {
      const normalizedOption = this.normalizeOption(option);
      this.selectedOption = normalizedOption;
      this.searchQuery = normalizedOption.cdNm;
      this.showDropdown = false;
      this.highlightedIndex = -1;
      this.lastCommittedOption = normalizedOption;

      // Prevent feedback loop by temporarily disabling the watcher
      this.isUserEditing = true;

      // emit Vue 3 v-model
      this.$emit("update:modelValue", normalizedOption.cd);
      this.$emit("change", normalizedOption);

      // Re-enable after a short delay to allow the modelValue to update
      this.$nextTick(() => {
        this.isUserEditing = false;
      });
    },

    selectFirstOption() {
      if (this.filteredOptions.length > 0) {
        this.selectOption(this.filteredOptions[0]);
      }
    },

    selectHighlightedOrFirst() {
      const list = this.filteredOptions;
      if (!list.length) return;

      const idx = this.highlightedIndex >= 0 ? this.highlightedIndex : 0;
      this.selectOption(list[idx]);
    },

    highlightNext() {
      if (!this.showDropdown) this.showDropdown = true;
      if (this.highlightedIndex < this.filteredOptions.length - 1) {
        this.highlightedIndex++;
      }
    },

    highlightPrevious() {
      if (!this.showDropdown) this.showDropdown = true;
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
      }
    },

    closeDropdown() {
      this.showDropdown = false;
      this.highlightedIndex = -1;
    },

    handleBlur() {
      // Delay closing to allow click events to fire
      setTimeout(() => {
        this.showDropdown = false;
        this.highlightedIndex = -1;

        // If user typed something but didn't select, try to find a match
        if (this.searchQuery && !this.selectedOption) {
          const match = this.options.find(
            (opt) =>
              opt.cdNm.toLowerCase() === this.searchQuery.toLowerCase() ||
              opt.cd.toLowerCase() === this.searchQuery.toLowerCase()
          );
          if (match) {
            this.selectOption(match);
          } else {
            // sensible fallback: auto-select if there's a single unambiguous filtered result
            const filtered = this.filteredOptions;
            if (filtered.length === 1) {
              this.selectOption(filtered[0]);
            }
          }
        } else if (!this.searchQuery && !this.selectedOption) {
          // Restore last committed selection unless the user intentionally cleared
          if (this.lastCommittedOption && !this.wasIntentionallyCleared) {
            this.selectedOption = this.lastCommittedOption;
            this.searchQuery = this.lastCommittedOption.cdNm;
            // keep parent model in sync when we auto-restore
            this.$emit("update:modelValue", this.lastCommittedOption.cd);
            this.$emit("change", this.lastCommittedOption);
          }
        }

        // Reset the flag after blur handling
        this.wasIntentionallyCleared = false;
      }, 150);
    },

    extractErrorMessage(error) {
      const responseData = error.response?.data;
      if (responseData?.error?.message) {
        return responseData.error.message;
      }
      if (responseData?.message) {
        return responseData.message;
      }
      if (error.message) {
        return error.message;
      }
      return "Failed to load options";
    },

    normalizeOptions(rawOptions) {
      if (!Array.isArray(rawOptions)) return [];
      return rawOptions
        .map((option) => this.normalizeOption(option))
        .filter((option) => option !== null);
    },

    normalizeOption(option) {
      if (!option || typeof option !== "object") return null;

      const normalizedCodeSource =
        option.cd ?? option.code ?? option.value ?? option.id ?? "";
      const normalizedNameSource =
        option.cdNm ?? option.name ?? option.label ?? option.cdDesc ?? "";

      const cd = String(normalizedCodeSource).trim();
      if (!cd) return null;

      const cdNm = String(normalizedNameSource || cd).trim();

      return {
        ...option,
        cd,
        cdNm,
      };
    },
  },
};
</script>
